import {announce} from '@github-ui/aria-live'
import {type BoardPaging, useBoardReaderMovement} from '@github-ui/board-core'
import {isResponseError} from '@github-ui/react-core/future/response-error'
import {hashKey, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {useCallback, useEffect, useRef, useState} from 'react'

import {appendAxisPage} from './append-axis-page'
import type {
  SavedViewsBoardAdapter,
  SavedViewsBoardAxis,
  SavedViewsBoardAxisPage,
  SavedViewsBoardAxisPageRequest,
  SavedViewsBoardData,
  SavedViewsBoardItem,
  SavedViewsBoardOptions,
  SavedViewsBoardPayload,
} from './types'

const NO_AXES: ReadonlySet<SavedViewsBoardAxis> = new Set()
export const MAX_PINNED_KEYS = 1_000
// The first request, and one retry against a pinned axis that grew while it was out.
const MAX_ATTEMPTS = 2

const PAGE_FAILED = 'Could not load more board groups. Try again.'
const AXIS_LIMIT_REACHED = 'Too many board groups are loaded to continue. Narrow the view and try again.'
const AXIS_FAILED = 'Could not load more board groups.'

type AxisRequest = {
  boardId: string
  requestId: number
  controller: AbortController
  settled: boolean
  cursor: string
  pinnedKeys: readonly string[]
  visiblePinnedKeys: readonly string[] | undefined
  /** Started before the reader asked, whatever has happened since, so a write invalidates it. */
  speculative: boolean
  /** Only a demanded request lights a skeleton, announces a failure, or holds the barrier. */
  demanded: boolean
}

/** `pinnedKeys` proves the other axis has not grown under a prefetched page. */
type BufferedPage<TItem extends SavedViewsBoardItem, TPageMetadata> = {
  cursor: string
  pinnedKeys: readonly string[]
  page: SavedViewsBoardAxisPage<TItem, TPageMetadata>
}

type BoardAxisPaging = Pick<BoardPaging, 'onLoadMoreColumns' | 'onLoadMoreRows' | 'onVisibleKeysChange'> & {
  loadingAxes: ReadonlySet<SavedViewsBoardAxis>
  /** Writes abort in-flight prefetches so stale counts cannot refill the slot. */
  invalidatePrefetchedPages: () => void
}

type AxisPageResult<TItem extends SavedViewsBoardItem, TPageMetadata> =
  | {ok: true; page: SavedViewsBoardAxisPage<TItem, TPageMetadata>}
  | {ok: false; error: unknown}

/** What the held board says to do with a page that came back. */
type AxisPageFit<TItem extends SavedViewsBoardItem, TMetadata> =
  | {kind: 'stale'}
  | {kind: 'retry'; pinnedKeys: string[]}
  | {kind: 'append'; payload: SavedViewsBoardPayload<TItem, TMetadata>}

function axisCursor<TItem extends SavedViewsBoardItem>(
  payload: SavedViewsBoardPayload<TItem, unknown>,
  axis: SavedViewsBoardAxis,
): string | null {
  return (axis === 'columns' ? payload.boardData.columnsCursor : payload.boardData.rowsCursor) ?? null
}

/**
 * Whether the axis has said it has a further page.
 *
 * A cursor on its own does not mean it does. A response may carry the cursor it ended at
 * alongside `hasMore: false`, and reading ahead on that asks a server that has already said
 * there is nothing there. The reader's own arrival cannot reach such an axis, because the
 * ghost it would arrive at is unmounted, so nothing else needs this check.
 */
function axisHasMore<TItem extends SavedViewsBoardItem>(
  payload: SavedViewsBoardPayload<TItem, unknown>,
  axis: SavedViewsBoardAxis,
): boolean {
  const {data} = payload.boardData
  return axis === 'columns' ? data.hasMoreColumns && !data.columnsTruncated : data.hasMoreRows && !data.rowsTruncated
}

function pinnedKeys<TItem extends SavedViewsBoardItem>(
  payload: SavedViewsBoardPayload<TItem, unknown>,
  axis: SavedViewsBoardAxis,
): string[] {
  const pinned = axis === 'columns' ? payload.boardData.data.rows : payload.boardData.data.columns
  return pinned.map(value => value.key)
}

function lastKey<TItem extends SavedViewsBoardItem>(
  payload: SavedViewsBoardPayload<TItem, unknown>,
  axis: SavedViewsBoardAxis,
): string | undefined {
  const values = axis === 'columns' ? payload.boardData.data.columns : payload.boardData.data.rows
  return values.at(-1)?.key
}

function otherAxis(axis: SavedViewsBoardAxis): SavedViewsBoardAxis {
  return axis === 'columns' ? 'rows' : 'columns'
}

function sameKeys(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((key, index) => key === right[index])
}

/**
 * A 422 (a refused key, a cursor minted for another field, too many item keys) and a 500 (a page
 * that cut a cell it was sized to fit) come back the same on every retry. A 503 or a network
 * failure may not.
 */
function failsEveryTime(error: unknown): boolean {
  return isResponseError(error) && (error.response.status === 422 || error.response.status === 500)
}

async function requestAxisPage<TItem extends SavedViewsBoardItem, TPageMetadata>(
  fetchAxisPage: NonNullable<SavedViewsBoardAdapter<TItem, unknown, TPageMetadata>['fetchAxisPage']>,
  request: SavedViewsBoardAxisPageRequest & SavedViewsBoardOptions,
): Promise<AxisPageResult<TItem, TPageMetadata>> {
  try {
    return {ok: true, page: await fetchAxisPage(request)}
  } catch (error) {
    return {ok: false, error}
  }
}

function fitAxisPage<TItem extends SavedViewsBoardItem, TMetadata, TPageMetadata>(
  held: SavedViewsBoardPayload<TItem, TMetadata> | undefined,
  {
    cursor,
    requestedPinnedKeys,
    page,
    appendMetadata,
  }: {
    cursor: string
    requestedPinnedKeys: readonly string[]
    page: SavedViewsBoardAxisPage<TItem, TPageMetadata>
    appendMetadata: SavedViewsBoardAdapter<TItem, TMetadata, TPageMetadata>['appendAxisMetadata']
  },
): AxisPageFit<TItem, TMetadata> {
  if (!held || axisCursor(held, page.axis) !== cursor) return {kind: 'stale'}

  const heldPinnedKeys = pinnedKeys(held, page.axis)
  if (!sameKeys(heldPinnedKeys, requestedPinnedKeys)) return {kind: 'retry', pinnedKeys: heldPinnedKeys}

  return {kind: 'append', payload: appendAxisPage(held, page, appendMetadata)}
}

function stopAxis<TItem extends SavedViewsBoardItem, TMetadata>(
  held: SavedViewsBoardPayload<TItem, TMetadata>,
  axis: SavedViewsBoardAxis,
): SavedViewsBoardPayload<TItem, TMetadata> {
  const {boardData} = held
  const {data} = boardData

  return {
    ...held,
    boardData:
      axis === 'columns'
        ? {...boardData, columnsCursor: null, data: {...data, hasMoreColumns: false, columnsTruncated: true}}
        : {...boardData, rowsCursor: null, data: {...data, hasMoreRows: false, rowsTruncated: true}},
  }
}

export function useBoardAxisPaging<TItem extends SavedViewsBoardItem, TMetadata, TPageMetadata>({
  adapter,
  options,
  queryKey,
}: {
  adapter: SavedViewsBoardAdapter<TItem, TMetadata, TPageMetadata>
  options: SavedViewsBoardOptions
  queryKey: QueryKey
}): BoardAxisPaging {
  const queryClient = useQueryClient()
  const [loadingAxes, setLoadingAxes] = useState<ReadonlySet<SavedViewsBoardAxis>>(NO_AXES)
  const requestsRef = useRef(new Map<SavedViewsBoardAxis, AxisRequest>())
  const bufferRef = useRef(new Map<SavedViewsBoardAxis, BufferedPage<TItem, TPageMetadata>>())
  // A failed prefetch spends only that cursor; demanded arrivals still retry it.
  const spentRef = useRef(new Map<SavedViewsBoardAxis, string>())
  // The renderer reports its window here, so a read taken before the reader reaches a boundary
  // still names the lanes on screen. Absent until it does, which a caller drawing none keeps.
  const visibleKeysRef = useRef(new Map<SavedViewsBoardAxis, readonly string[] | undefined>())
  // `ensureBuffered` is declared below the reads that refill through it, and a read has to
  // reach it after `finish` has released the record it is blocked on.
  const refillRef = useRef<((axis: SavedViewsBoardAxis) => void) | null>(null)
  const lastRequestIdRef = useRef(0)
  const boardId = hashKey(queryKey)
  const {appendAxisMetadata, fetchAxisPage} = adapter
  const {query} = options

  // Outside `BoardProvider`, so this hears input anywhere on the page. It errs safe: it can
  // read a page ahead for a reader scrolling something else, never miss one for this reader.
  const {subscribeToInput} = useBoardReaderMovement()

  const held = useCallback(
    () => queryClient.getQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey),
    [queryClient, queryKey],
  )

  const markLoading = useCallback((axis: SavedViewsBoardAxis) => {
    setLoadingAxes(previous => (previous.has(axis) ? previous : new Set(previous).add(axis)))
  }, [])

  const clearLoading = useCallback((axis: SavedViewsBoardAxis) => {
    setLoadingAxes(previous => {
      if (!previous.has(axis)) return previous
      const remaining = new Set(previous)
      remaining.delete(axis)
      return remaining
    })
  }, [])

  useEffect(() => {
    const outstanding = requestsRef.current
    const buffered = bufferRef.current
    const spent = spentRef.current
    const visible = visibleKeysRef.current
    return () => {
      for (const request of outstanding.values()) request.controller.abort()
      outstanding.clear()
      buffered.clear()
      spent.clear()
      visible.clear()
      setLoadingAxes(NO_AXES)
    }
  }, [boardId])

  const owns = useCallback((axis: SavedViewsBoardAxis, request: AxisRequest) => {
    const current = requestsRef.current.get(axis)
    return current?.boardId === request.boardId && current.requestId === request.requestId
  }, [])

  const finish = useCallback(
    (axis: SavedViewsBoardAxis, request: AxisRequest) => {
      if (!owns(axis, request)) return
      request.settled = true

      if (!request.demanded) {
        requestsRef.current.delete(axis)
        return
      }

      // Keep both directions blocked until an overlapping pair and its reconciliation retry settle.
      // Only the demanded ones: a prefetch would hold a landed page's skeleton on screen.
      if ([...requestsRef.current.values()].some(current => current.demanded && !current.settled)) return

      for (const [key, current] of [...requestsRef.current]) {
        if (current.demanded) requestsRef.current.delete(key)
      }
      setLoadingAxes(NO_AXES)
    },
    [owns],
  )

  const startRequest = useCallback(
    (
      axis: SavedViewsBoardAxis,
      cursor: string,
      keys: readonly string[],
      {
        demanded,
        speculative,
        visiblePinnedKeys,
      }: {demanded: boolean; speculative: boolean; visiblePinnedKeys: readonly string[] | undefined},
    ) => {
      const requestId = (lastRequestIdRef.current += 1)
      const request: AxisRequest = {
        boardId,
        requestId,
        controller: new AbortController(),
        settled: false,
        cursor,
        pinnedKeys: keys,
        visiblePinnedKeys,
        demanded,
        speculative,
      }

      requestsRef.current.set(axis, request)
      if (demanded) markLoading(axis)

      return request
    },
    [boardId, markLoading],
  )

  const stopPaging = useCallback(
    (axis: SavedViewsBoardAxis, cursor: string, message: string) => {
      const current = held()
      if (!current || axisCursor(current, axis) !== cursor) return

      queryClient.setQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey, stopAxis(current, axis))
      announce(message)
    },
    [held, queryClient, queryKey],
  )

  // A held page lacks cells for what the other axis gained since, so serving it means a retry
  // behind a skeleton. Read again once the reader heads its way, not on every growth.
  const refreshIfStale = useCallback(
    (axis: SavedViewsBoardAxis) => {
      const next = bufferRef.current.get(axis)
      const current = held()
      if (!next || !current || requestsRef.current.has(axis)) return
      if (sameKeys(next.pinnedKeys, pinnedKeys(current, axis))) return

      bufferRef.current.delete(axis)
      refillRef.current?.(axis)
    },
    [held],
  )

  // An axis's window arrives as the lanes the other axis reads against.
  const refreshIfAtEnd = useCallback(
    (axis: SavedViewsBoardAxis) => {
      const current = held()
      const last = current && lastKey(current, axis)
      if (last !== undefined && visibleKeysRef.current.get(otherAxis(axis))?.includes(last)) refreshIfStale(axis)
    },
    [held, refreshIfStale],
  )

  // The other axis's held page is kept until the reader heads its way (`refreshIfStale`).
  // Dropping it on every commit cost a reader going straight down one unused column read per
  // swimlane page.
  const commitAxisPage = useCallback(
    (payload: SavedViewsBoardPayload<TItem, TMetadata>) => {
      queryClient.setQueryData<SavedViewsBoardPayload<TItem, TMetadata>>(queryKey, payload)
    },
    [queryClient, queryKey],
  )

  const planPrefetch = useCallback(
    (axis: SavedViewsBoardAxis) => {
      if (!fetchAxisPage) return null

      // One in-flight request or one held page per axis, which is both the memory bound and
      // the runaway bound. A second page only helps a reader who crosses two boundaries without
      // pausing, and it cannot be asked for until the first returns with its cursor.
      if (requestsRef.current.has(axis)) return null
      if (bufferRef.current.has(axis)) return null

      const current = held()
      if (!current) return null
      if (!axisHasMore(current, axis)) return null

      const cursor = axisCursor(current, axis)
      if (cursor === null) return null
      if (spentRef.current.get(axis) === cursor) return null

      const keys = pinnedKeys(current, axis)
      // Speculative reads fail silently; demanded reads surface and stop the axis.
      if (keys.length > MAX_PINNED_KEYS) return null

      return {cursor, keys}
    },
    [fetchAxisPage, held],
  )

  // `true` means a held page settled the demand, including a successful fold or reported failure.
  const consume = useCallback(
    (axis: SavedViewsBoardAxis) => {
      const next = bufferRef.current.get(axis)
      if (!next) return false

      const current = held()
      // Dropped before the fold rather than after it, so a fold that throws cannot leave the
      // page in the slot to be folded, and thrown, again.
      bufferRef.current.delete(axis)

      if (!current || axisCursor(current, axis) !== next.cursor) return false

      let fit
      try {
        fit = fitAxisPage(current, {
          cursor: next.cursor,
          requestedPinnedKeys: next.pinnedKeys,
          page: next.page,
          appendMetadata: appendAxisMetadata,
        })
      } catch {
        // `appendAxisMetadata` is caller code; a thrown held fold reports like a demanded failure.
        announce(PAGE_FAILED)
        clearLoading(axis)
        return true
      }

      // Pinned keys changed while held; the demanded path must fetch the new corner cells.
      if (fit.kind !== 'append') return false

      commitAxisPage(fit.payload)
      clearLoading(axis)
      refreshIfAtEnd(otherAxis(axis))
      return true
    },
    [appendAxisMetadata, clearLoading, commitAxisPage, held, refreshIfAtEnd],
  )

  const runDemanded = useCallback(
    async (
      axis: SavedViewsBoardAxis,
      request: AxisRequest,
      visiblePinnedKeys: readonly string[] | undefined,
      stop: (message: string) => void,
      fetchPage: NonNullable<SavedViewsBoardAdapter<TItem, TMetadata, TPageMetadata>['fetchAxisPage']>,
    ) => {
      let requestedPinnedKeys = request.pinnedKeys
      let failed = false
      let committed = false

      // No `throw` in here: React Compiler cannot lower one inside `try`. The catch is for an
      // unexpected exception, so the loading barrier still comes down.
      try {
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
          const result = await requestAxisPage(fetchPage, {
            axis,
            cursor: request.cursor,
            pinnedKeys: requestedPinnedKeys,
            // Unchanged on the retry. Adding every lane the other axis just brought would split the
            // card budget across lanes that may not be drawn.
            visiblePinnedKeys,
            query,
            signal: request.controller.signal,
          })
          // An abort clears the record too, so this also covers a board that was left.
          if (!owns(axis, request)) return

          if (!result.ok && failsEveryTime(result.error)) {
            stop(AXIS_FAILED)
            break
          }
          if (!result.ok || result.page.axis !== axis) {
            failed = true
            break
          }

          const fit = fitAxisPage(held(), {
            cursor: request.cursor,
            requestedPinnedKeys,
            page: result.page,
            appendMetadata: appendAxisMetadata,
          })
          if (fit.kind === 'append') {
            commitAxisPage(fit.payload)
            committed = true
          }
          if (fit.kind !== 'retry') break

          if (fit.pinnedKeys.length > MAX_PINNED_KEYS) {
            stop(AXIS_LIMIT_REACHED)
            break
          }
          // The pinned axis grew again while the retry was out.
          if (attempt === MAX_ATTEMPTS) {
            failed = true
            break
          }
          requestedPinnedKeys = fit.pinnedKeys
        }
      } catch {
        failed = true
      }

      if (failed && !request.controller.signal.aborted) announce(PAGE_FAILED)
      finish(axis, request)

      // After `finish`, which releases the record `planPrefetch` refuses to read behind. Both
      // axes: this one's slot was spent, and the other's may be empty.
      //
      // Only on a commit: a read that failed leaves its cursor in place, and reading it again
      // here would retry on the reader's behalf without them asking.
      if (committed) {
        refillRef.current?.(axis)
        refreshIfAtEnd(otherAxis(axis))
        refillRef.current?.(otherAxis(axis))
      }
    },
    [appendAxisMetadata, commitAxisPage, finish, held, owns, query, refreshIfAtEnd],
  )

  const serveDemand = useCallback(
    (axis: SavedViewsBoardAxis, visiblePinnedKeys: readonly string[] | undefined) => {
      if (!fetchAxisPage) return

      if (visiblePinnedKeys !== undefined) visibleKeysRef.current.set(axis, visiblePinnedKeys)

      const current = held()
      if (!current) return clearLoading(axis)
      const cursor = axisCursor(current, axis)
      if (cursor === null) return clearLoading(axis)

      // A valid held page avoids the visible loading state entirely.
      if (consume(axis)) return

      const outstanding = requestsRef.current.get(axis)
      // A settled one is kept only to hold the cross-axis barrier, and nothing is coming from
      // it. Treating it as in flight swallows the demand, since nothing replays it later.
      if (outstanding && !outstanding.settled) {
        // Adopt the in-flight prefetch so the reader waits on its skeleton instead of a duplicate.
        if (!outstanding.demanded) {
          outstanding.demanded = true
          markLoading(axis)
        }

        // These keys are for a replacement if a write discards this promoted prefetch.
        outstanding.visiblePinnedKeys = visiblePinnedKeys
        return
      }

      const keys = pinnedKeys(current, axis)
      const stop = (message: string) => stopPaging(axis, cursor, message)
      if (keys.length > MAX_PINNED_KEYS) {
        clearLoading(axis)
        stop(AXIS_LIMIT_REACHED)
        return
      }

      const request = startRequest(axis, cursor, keys, {
        demanded: true,
        speculative: false,
        visiblePinnedKeys,
      })
      void runDemanded(axis, request, visiblePinnedKeys, stop, fetchAxisPage)
    },
    [clearLoading, consume, fetchAxisPage, held, markLoading, runDemanded, startRequest, stopPaging],
  )

  // Safe on every input event: once a request is out or a slot is full, calls are map lookups.
  // The reader's distance and speed do not change which page follows the visible one.
  const ensureBuffered = useCallback(
    (axis: SavedViewsBoardAxis) => {
      const plan = planPrefetch(axis)
      if (plan === null || !fetchAxisPage) return

      const visiblePinnedKeys = visibleKeysRef.current.get(axis)
      const request = startRequest(axis, plan.cursor, plan.keys, {
        demanded: false,
        speculative: true,
        visiblePinnedKeys,
      })

      const read = async () => {
        const result = await requestAxisPage(fetchAxisPage, {
          axis,
          cursor: plan.cursor,
          pinnedKeys: plan.keys,
          visiblePinnedKeys,
          query,
          signal: request.controller.signal,
        })
        if (!owns(axis, request)) return

        if (result.ok && result.page.axis === axis) {
          bufferRef.current.set(axis, {cursor: plan.cursor, pinnedKeys: plan.keys, page: result.page})
        } else {
          // Spend only speculative failures; demanded arrivals still retry through `serveDemand`.
          spentRef.current.set(axis, plan.cursor)
        }

        // Capture demanded state before `finish`; the reader may have supplied replacement
        // keys while this prefetch was in flight.
        const wanted = request.demanded
        finish(axis, request)
        // The other axis may have grown while this was out, with nothing held here to refresh.
        if (wanted) serveDemand(axis, request.visiblePinnedKeys)
        else refreshIfAtEnd(axis)

        refillRef.current?.(axis)
        refillRef.current?.(otherAxis(axis))
      }

      void read()
    },
    [fetchAxisPage, finish, owns, planPrefetch, query, refreshIfAtEnd, serveDemand, startRequest],
  )

  useEffect(() => {
    refillRef.current = ensureBuffered
  }, [ensureBuffered])

  // The renderer's window, which is the only place the lanes an axis reads against come from.
  // It arrives before the reader reaches any boundary, and is what lets the first read carry
  // them. The lanes are the other axis's window, so a change is the reader moving along that axis.
  const onVisibleKeysChange = useCallback(
    (axis: SavedViewsBoardAxis, visibleKeys: readonly string[]) => {
      const known = visibleKeysRef.current.get(axis)
      if (known !== undefined && sameKeys(known, visibleKeys)) return

      visibleKeysRef.current.set(axis, visibleKeys)
      ensureBuffered(axis)
      refreshIfStale(otherAxis(axis))
    },
    [ensureBuffered, refreshIfStale],
  )

  const invalidatePrefetchedPages = useCallback(() => {
    bufferRef.current.clear()
    spentRef.current.clear()

    // Including the ones the reader now waits on: a promoted prefetch would otherwise put the
    // discarded data straight back in the slot. Those are asked again below.
    const waiting: Array<[SavedViewsBoardAxis, readonly string[] | undefined]> = []
    for (const [axis, request] of [...requestsRef.current]) {
      if (!request.speculative) continue

      request.controller.abort()
      requestsRef.current.delete(axis)
      // A settled one is only held to keep the cross-axis barrier up; its page has already
      // landed. Replaying it would append the page after it without the reader arriving.
      if (request.demanded && !request.settled) waiting.push([axis, request.visiblePinnedKeys])
    }

    for (const [axis, keys] of waiting) {
      serveDemand(axis, keys)

      // The landing invalidation must discard this replacement too, so the reader gets a
      // post-write page.
      const replacement = requestsRef.current.get(axis)
      if (replacement !== undefined) replacement.speculative = true
    }

    ensureBuffered('columns')
    ensureBuffered('rows')
  }, [ensureBuffered, serveDemand])

  // Initial prefetch pays the first round trip before the reader reaches the end.
  // `boardId` prevents a held page from one view being inherited by another.
  useEffect(() => {
    ensureBuffered('columns')
    ensureBuffered('rows')
  }, [boardId, ensureBuffered])

  // Continuous scroll may never rest, so input keeps both one-page slots warm.
  // Gesture events do not reveal which axis moved; checking both costs two map lookups.
  useEffect(
    () =>
      subscribeToInput(() => {
        ensureBuffered('columns')
        ensureBuffered('rows')
      }),
    [ensureBuffered, subscribeToInput],
  )

  const onLoadMoreColumns = useCallback(
    (visibleRowKeys?: readonly string[]) => {
      serveDemand('columns', visibleRowKeys)
      // Refill both slots: this axis was consumed, and the other was invalidated by growth.
      ensureBuffered('columns')
      ensureBuffered('rows')
    },
    [ensureBuffered, serveDemand],
  )
  const onLoadMoreRows = useCallback(
    (visibleColumnKeys?: readonly string[]) => {
      serveDemand('rows', visibleColumnKeys)
      ensureBuffered('rows')
      ensureBuffered('columns')
    },
    [ensureBuffered, serveDemand],
  )

  return {
    onLoadMoreColumns: fetchAxisPage ? onLoadMoreColumns : undefined,
    onLoadMoreRows: fetchAxisPage ? onLoadMoreRows : undefined,
    onVisibleKeysChange: fetchAxisPage ? onVisibleKeysChange : undefined,
    loadingAxes,
    invalidatePrefetchedPages,
  }
}

export function withLoadingAxes<TItem extends SavedViewsBoardItem>(
  boardData: SavedViewsBoardData<TItem>,
  loadingAxes: ReadonlySet<SavedViewsBoardAxis>,
): SavedViewsBoardData<TItem> {
  if (loadingAxes.size === 0) return boardData

  return {
    ...boardData,
    data: {
      ...boardData.data,
      isLoadingColumns: loadingAxes.has('columns'),
      isLoadingRows: loadingAxes.has('rows'),
    },
  }
}
